import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Box,
  Switch,
  FormControlLabel,
  Link,
  TextField,
  Stack,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import { AdminPanelSettings as AdminPanelSettingsIcon, ArrowBack as ArrowBackIcon, Store as StoreIcon, LocationCity as LocationCityIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { normalizeImageUrl } from '../services/imageUtils';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { currentUser } = useAuth();
  const api = useApi();
  const [userRoles, setUserRoles] = useState({});
  const navigate = useNavigate();

  // Pagination + filters
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sort, setSort] = useState('id,desc');
  // eslint-disable-next-line no-unused-vars
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filterMode, setFilterMode] = useState('all'); // all | country | city | nameOrEmail
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [nameOrEmailQuery, setNameOrEmailQuery] = useState('');
  const [nameOrEmailInput, setNameOrEmailInput] = useState('');
  const [localQuery, setLocalQuery] = useState('');

  // Make Provider dialog
  const [makeProviderDialogOpen, setMakeProviderDialogOpen] = useState(false);
  const [makeProviderUser, setMakeProviderUser] = useState(null);
  const [providerType, setProviderType] = useState('');
  const [providerTitle, setProviderTitle] = useState('');
  const [providerAbout, setProviderAbout] = useState('');
  const [providerInstagram, setProviderInstagram] = useState('');
  const [providerYears, setProviderYears] = useState('');
  const [providerDocUrl, setProviderDocUrl] = useState('');
  const [savingProvider, setSavingProvider] = useState(false);
  const [makeProviderError, setMakeProviderError] = useState('');
  const [makeProviderEditing, setMakeProviderEditing] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      let data;
      if (filterMode === 'country' && country.trim()) {
        data = await api.user.searchUsersByCountry({ country: country.trim(), page, size: rowsPerPage, sort });
      } else if (filterMode === 'city' && city.trim()) {
        data = await api.user.searchUsersByCity({ city: city.trim(), page, size: rowsPerPage, sort });
      } else if (filterMode === 'nameOrEmail' && nameOrEmailQuery.trim()) {
        data = await api.user.searchUsersByNameOrEmail({ q: nameOrEmailQuery.trim(), page, size: rowsPerPage, sort });
      } else {
        data = await api.user.getAllUsers({ page, size: rowsPerPage, sort });
      }

      const content = Array.isArray(data) ? data : (data?.content || []);

      // Transform the data to match the component's expected format
      const formattedUsers = content.map(user => ({
        id: user.id,
        authId: user.authId,
        email: user.email,
        name: user.name,
        picture: user.picture,
        phone: user.phone,
        address: user.address,
        emailVerified: user.emailVerified || false,
        suspended: user.suspended,
        userState: user.userState,
        createdAt: user.createdAt,
        updatedAt: user.updatedAt,
        dogOwnerProfile: user.dogOwnerProfile,
        provider: user.provider,
        country: user.country,
        city: user.city
      }));

      setUsers(formattedUsers);
      if (data && !Array.isArray(data)) {
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
      } else {
        setTotalPages(1);
        setTotalElements(formattedUsers.length);
      }

      // Fetch roles for each user concurrently using Promise.all
      const rolesData = {};
      const rolePromises = formattedUsers.map(async (user) => {
        if (user.authId) {
          try {
            const claims = await api.user.getUserClaims(user.authId);
            if (claims && claims.roles) {
              return { id: user.id, roles: claims.roles };
            }
          } catch (error) {
            console.error(`Error fetching roles for user ${user.id}:`, error);
          }
        }
        return null;
      });

      const results = await Promise.all(rolePromises);
      results.forEach(result => {
        if (result) {
          rolesData[result.id] = result.roles;
        }
      });

      setUserRoles(rolesData);

    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sort, filterMode, nameOrEmailQuery]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleSuspend = async (userId) => {
    try {
      setError('');
      const user = users.find(u => u.id === userId);
      if (!user) return;

      // Get the current user data to ensure we have the latest status
      const userData = await api.user.getUserById(userId);

      if(userData.suspended){
        await api.user.unsuspend(userId);
      } else {
        await api.user.suspend(userId);
      }

      // Update the local state
      setUsers(users.map(u =>
          u.id === userId ? { ...u, suspended: !u.suspended } : u
      ));

      setSuccessMessage(`User ${user.email} has been ${!user.suspended ? 'suspended' : 'unsuspended'}.`);
    } catch (err) {
      console.error('Error toggling user suspension:', err);
      setError('Failed to update user status. Please try again.');
    }

    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleToggleAdmin = async (userId) => {
    try {
      setError('');
      const user = users.find(u => u.id === userId);
      if (!user) return;

      await api.user.addAdminRole(userId);

      // Update the local state immutably
      setUserRoles(prev => {
        const currentRoles = prev[userId] || [];
        if (!currentRoles.includes('admin')) {
          return {
            ...prev,
            [userId]: [...currentRoles, 'admin']
          };
        }
        return prev;
      });

      setUsers(users.map(u =>
          u.id === userId ? { ...u, admin: true } : u
      ));

      setSuccessMessage(`User ${user.email} is now an admin.`);
    } catch (err) {
      console.error('Error adding admin role:', err);
      setError('Failed to update admin role. Please try again.');
    }

    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const openMakeProviderDialog = (user) => {
    setMakeProviderUser(user);
    // Pre-fill from the existing provider when present so an admin can override it.
    const existing = (user.provider && typeof user.provider === 'object') ? user.provider : null;
    const alreadyProvider = !!existing || user.provider === true || (userRoles[user.id]?.includes('provider') ?? false);
    setMakeProviderEditing(alreadyProvider);
    setProviderType(existing?.providerType || '');
    setProviderTitle(existing?.title || '');
    setProviderAbout(existing?.about || '');
    setProviderInstagram(existing?.instagramUser || '');
    setProviderYears(existing?.yearsOfExperience != null ? String(existing.yearsOfExperience) : '');
    setProviderDocUrl(existing?.documentUrl || '');
    setMakeProviderError('');
    setMakeProviderDialogOpen(true);
  };

  const closeMakeProviderDialog = () => {
    setMakeProviderDialogOpen(false);
    setMakeProviderUser(null);
    setMakeProviderError('');
  };

  const handleCreateProvider = async () => {
    if (!makeProviderUser || !providerType || !providerTitle.trim()) return;
    setSavingProvider(true);
    setMakeProviderError('');
    try {
      await api.providers.createProviderForUser({
        userId: makeProviderUser.id,
        providerType,
        title: providerTitle.trim(),
        about: providerAbout.trim() || null,
        instagramUser: providerInstagram.trim() || null,
        yearsOfExperience: providerYears !== '' ? Number(providerYears) : null,
        documentUrl: providerDocUrl.trim() || null,
        serviceTypes: [],
      });
      closeMakeProviderDialog();
      setSuccessMessage(
        makeProviderEditing
          ? `Provider details for ${makeProviderUser.email} were updated.`
          : `User ${makeProviderUser.email} is now a provider.`
      );
      setTimeout(() => setSuccessMessage(''), 3000);
      // Refresh the list so provider status and roles reflect the change
      fetchUsers();
    } catch (err) {
      console.error('Error creating provider:', err);
      const msg = err?.message || '';
      // Surface backend message if present (e.g. "User already has a provider")
      const backendMsg = msg.replace(/^API Error:\s*\d+\s*[^—]*—?\s*/i, '').trim();
      setMakeProviderError(backendMsg || 'Failed to create provider. Please try again.');
    } finally {
      setSavingProvider(false);
    }
  };

  const handleUserClick = (userId) => {
    navigate(`/users/${userId}`);
  };

  if (loading) {
    return (
        <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
    );
  }

  return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
          Dashboard
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Users Management
          </Typography>
          <Box />
        </Box>

        {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
        )}

        {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
        )}

        {/* Filters and search */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack spacing={2}>
            {/* Server-side name/email search */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <TextField
                  label="Search by name or email"
                  placeholder="e.g. John or john@example.com"
                  value={nameOrEmailInput}
                  onChange={(e) => setNameOrEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && nameOrEmailInput.trim()) {
                      setFilterMode('nameOrEmail');
                      setNameOrEmailQuery(nameOrEmailInput.trim());
                      setPage(0);
                    }
                  }}
                  size="small"
                  sx={{ minWidth: 320 }}
              />
              <Button
                  variant={filterMode === 'nameOrEmail' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFilterMode('nameOrEmail');
                    setNameOrEmailQuery(nameOrEmailInput.trim());
                    setPage(0);
                  }}
                  disabled={!nameOrEmailInput.trim()}
              >
                Search
              </Button>
              {filterMode === 'nameOrEmail' && (
                  <Button onClick={() => {
                    setFilterMode('all');
                    setNameOrEmailInput('');
                    setNameOrEmailQuery('');
                    setPage(0);
                  }}>
                    Clear search
                  </Button>
              )}
              <Box flexGrow={1} />
              <TextField
                  label="Local search (on page)"
                  placeholder="Filter rows shown below"
                  value={localQuery}
                  onChange={(e) => setLocalQuery(e.target.value)}
                  size="small"
                  sx={{ minWidth: 240 }}
              />
            </Stack>

            {/* Country / city filters */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <TextField
                  label="Country filter (server)"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  size="small"
              />
              <Button variant={filterMode === 'country' ? 'contained' : 'outlined'} onClick={() => { setFilterMode('country'); setPage(0); }} disabled={!country.trim()}>Apply Country</Button>
              <TextField
                  label="City filter (server)"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  size="small"
              />
              <Button variant={filterMode === 'city' ? 'contained' : 'outlined'} onClick={() => { setFilterMode('city'); setPage(0); }} disabled={!city.trim()}>Apply City</Button>
              <Button onClick={() => {
                setFilterMode('all');
                setCountry('');
                setCity('');
                setNameOrEmailInput('');
                setNameOrEmailQuery('');
                setPage(0);
              }}>
                Clear All Filters
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ height: '75vh', overflow: 'auto' }}>
            <Table stickyHeader aria-label="sticky table" sx={{ minWidth: 1600 }}>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Auth ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Picture</TableCell>
                  <TableCell>Email Verified</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Suspended</TableCell>
                  <TableCell>Dog Owner Profile</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users
                    .filter(u => {
                      const q = localQuery.trim().toLowerCase();
                      if (!q) return true;
                      return (
                          (u.name || '').toLowerCase().includes(q) ||
                          (u.email || '').toLowerCase().includes(q) ||
                          (u.authId || '').toLowerCase().includes(q) ||
                          (u.country || '').toLowerCase().includes(q) ||
                          (u.city || '').toLowerCase().includes(q)
                      );
                    })
                    .map((user) => {
                      const roles = userRoles[user.id] || [];
                      const isAdmin = roles.includes('admin');
                      const isProvider = roles.includes('provider');
                      const isCityManager = roles.includes('city_manager');

                      return (
                          <TableRow hover key={user.id}>
                            <TableCell>
                              <Link
                                  component="button"
                                  variant="body2"
                                  onClick={() => handleUserClick(user.id)}
                                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                              >
                                {user.id}
                              </Link>
                            </TableCell>
                            <TableCell>{user.authId}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.name}</TableCell>
                            <TableCell>{user.phone || 'N/A'}</TableCell>
                            <TableCell>{user.country || 'N/A'}</TableCell>
                            <TableCell>{user.city || 'N/A'}</TableCell>
                            <TableCell>{user.address || 'N/A'}</TableCell>
                            <TableCell>
                              {user.picture ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <img
                                        src={normalizeImageUrl(user.picture)}
                                        alt={`${user.name}'s profile`}
                                        style={{ width: 40, height: 40, borderRadius: '50%', marginRight: 8 }}
                                    />
                                  </Box>
                              ) : 'No picture'}
                            </TableCell>
                            <TableCell>
                              <FormControlLabel
                                  control={
                                    <Switch
                                        checked={user.emailVerified}
                                        disabled={true} // Read-only
                                        color="primary"
                                    />
                                  }
                                  label={user.emailVerified ? "Verified" : "Not Verified"}
                              />
                            </TableCell>
                            <TableCell>{user.userState}</TableCell>
                            <TableCell>
                              <FormControlLabel
                                  control={
                                    <Switch
                                        checked={!user.suspended}
                                        onChange={() => handleToggleSuspend(user.id)}
                                        color="primary"
                                        disabled={user.email === currentUser?.email} // Can't suspend yourself
                                    />
                                  }
                                  label={user.suspended ? "Suspended" : "Active"}
                              />
                            </TableCell>
                            <TableCell>{user.dogOwnerProfile ? 'Yes' : 'No'}</TableCell>
                            <TableCell>{user.provider ? 'Yes' : 'No'}</TableCell>
                            <TableCell>
                              {roles.length > 0 ? roles.join(', ') : 'No roles'}
                            </TableCell>
                            <TableCell>
                              <Tooltip title={isAdmin ? "Already Admin" : "Make Admin"}>
                          <span>
                            <IconButton
                                size="small"
                                color="primary"
                                disabled={isAdmin || user.email === currentUser?.email}
                                onClick={() => handleToggleAdmin(user.id)}
                            >
                              <AdminPanelSettingsIcon fontSize="small" />
                            </IconButton>
                          </span>
                              </Tooltip>
                              <Tooltip title={isProvider ? "Edit provider details" : "Make Provider"}>
                          <span>
                            <IconButton
                                size="small"
                                color="secondary"
                                disabled={user.email === currentUser?.email}
                                onClick={() => openMakeProviderDialog(user)}
                            >
                              <StoreIcon fontSize="small" />
                            </IconButton>
                          </span>
                              </Tooltip>
                              {isCityManager && (
                                <Tooltip title="City Manager (manage assigned cities in user details)">
                                  <span>
                                    <IconButton size="small" color="info" disabled>
                                      <LocationCityIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
              rowsPerPageOptions={[5, 10, 20, 50]}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            User data is fetched from the backend API. Changes to user status and admin privileges are saved to the database.
          </Typography>
        </Box>

        {/* Make Provider dialog */}
        <Dialog open={makeProviderDialogOpen} onClose={savingProvider ? undefined : closeMakeProviderDialog} fullWidth maxWidth="sm">
          <DialogTitle>{makeProviderEditing ? 'Edit provider' : 'Make provider'}</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2, pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>User:</strong> {makeProviderUser?.name} ({makeProviderUser?.email})
              </Typography>
            </Box>

            {makeProviderError && (
              <Alert severity="error" sx={{ mb: 2 }}>{makeProviderError}</Alert>
            )}

            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel id="provider-type-label">Provider type</InputLabel>
              <Select
                labelId="provider-type-label"
                label="Provider type"
                value={providerType}
                onChange={(e) => setProviderType(e.target.value)}
              >
                <MenuItem value="SERVICE">SERVICE</MenuItem>
                <MenuItem value="INFLUENCER">INFLUENCER</MenuItem>
                <MenuItem value="STORE">STORE</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              required
              label="Title"
              value={providerTitle}
              onChange={(e) => setProviderTitle(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="About (optional)"
              value={providerAbout}
              onChange={(e) => setProviderAbout(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Instagram user (optional)"
              value={providerInstagram}
              onChange={(e) => setProviderInstagram(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="number"
              label="Years of experience (optional)"
              value={providerYears}
              onChange={(e) => setProviderYears(e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Document URL (optional)"
              value={providerDocUrl}
              onChange={(e) => setProviderDocUrl(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeMakeProviderDialog} disabled={savingProvider}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreateProvider}
              disabled={savingProvider || !providerType || !providerTitle.trim()}
            >
              {savingProvider ? 'Saving…' : (makeProviderEditing ? 'Save changes' : 'Create provider')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
  );
};

export default Users;